/**
 * Strips ?variant= parameter from all product card links inside Nosto placement containers.
 * Uses MutationObserver to catch dynamically injected Nosto content.
 *
 * Also closes the cart drawer before navigating when a recommendation tile inside
 * #bag-drawer-recommendations is clicked, and cleans up the open-drawer state in bfcache.
 */
(function () {
  'use strict';

  var NOSTO_IDS = ['productpage-nosto-2', 'productpage-nosto-3', 'bag-recommendations', 'oli-rec-nosto', 'searchpage', 'searchpage-no-results','categorypage-nosto-1', 'bag-drawer-recommendations'];

  function stripVariantFromLinks(container) {
    var links = container.querySelectorAll('a[href*="variant"]');
    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      try {
        var url = new URL(link.href);
        if (url.searchParams.has('variant')) {
          url.searchParams.delete('variant');
          link.href = url.toString();
        }
      } catch (e) {
        // skip malformed URLs
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Drawer-close-before-navigation
  // ---------------------------------------------------------------------------
  // Registered on `document` in capture phase so it fires before any
  // element-level handlers (including Nosto's own click-to-navigate). This
  // survives dynamic replacement of #bag-drawer-recommendations because we
  // check the event path at click time rather than storing an element ref.

  function setupDrawerCloseOnNavigation() {
    document.addEventListener('click', function (e) {
      // Only plain left-clicks (ctrl/cmd/middle → new tab should work normally)
      if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey) return;

      var target = /** @type {Element | null} */ (e.target);
      if (!target || typeof target.closest !== 'function') return;

      // Exclude <button> clicks (slideshow prev/next arrows, wishlist button, ATC).
      // We use only the 'button' HTML element — NOT [role="button"] — because
      // nosto-dynamic-card or product-card may carry role="button" on the outer
      // wrapper element, which would silently swallow every tile click.
      if (target.closest('button')) return;

      // Walk the event path to find the bag-recs container and a product link.
      // composedPath() sees through Shadow DOM boundaries; fall back to target.
      var link = /** @type {HTMLAnchorElement | null} */ (null);
      var inBagRecs = false;
      var path = e.composedPath ? e.composedPath() : [target];

      for (var i = 0; i < path.length; i++) {
        var node = /** @type {any} */ (path[i]);
        if (!node || typeof node !== 'object') continue;

        if (node.id === 'bag-drawer-recommendations') {
          inBagRecs = true;
          break; // don't look for links above the rec container
        }

        // Only intercept product-page links (full URL — node.pathname is the
        // path portion only, e.g. "/products/…" for absolute hrefs).
        if (!link && node instanceof HTMLAnchorElement && node.href &&
            node.pathname && node.pathname.startsWith('/products/')) {
          link = node;
        }
      }

      if (!inBagRecs) return;

      // Fallback: the clicked element may be product text (brand, title, price)
      // that sits OUTSIDE the <a class="contents"> image link but still inside
      // product-card. product-card has its own click handler that delegates to
      // the primary link (ref="productCardLink"). Find that link by querying
      // upward through the path so we can navigate after the drawer closes.
      //
      // IMPORTANT: only run this fallback when the click is genuinely inside a
      // product card. Swiper prev/next navigation arrows are NOT inside a product
      // card but ARE inside swiper-container (which itself contains slides with
      // product links). Without this guard, clicking an arrow would cause
      // ancestor.querySelector to find a link in a sibling slide, close the
      // drawer, and navigate to the PDP unexpectedly.
      if (!link) {
        var inProductCard = false;
        for (var k = 0; k < path.length; k++) {
          var pNode = /** @type {any} */ (path[k]);
          if (!pNode || typeof pNode !== 'object') continue;
          if (pNode.id === 'bag-drawer-recommendations') break;
          var tag = pNode.tagName && pNode.tagName.toLowerCase();
          if (tag === 'product-card' || tag === 'nosto-dynamic-card') {
            inProductCard = true;
            break;
          }
        }

        if (inProductCard) {
          for (var j = 0; j < path.length; j++) {
            var ancestor = /** @type {any} */ (path[j]);
            if (!ancestor || typeof ancestor.querySelector !== 'function') continue;
            var found = /** @type {HTMLAnchorElement | null} */ (ancestor.querySelector('a[href*="/products/"]'));
            if (found && found.pathname && found.pathname.startsWith('/products/')) {
              link = found;
              break;
            }
            if (ancestor.id === 'bag-drawer-recommendations') break;
          }
        }
      }

      if (!link) return;

      var drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
      if (!drawer || typeof drawer.close !== 'function') return;

      // Prevent the browser from following the link AND stop any Nosto click
      // handler from calling window.location.href before we're ready.
      e.preventDefault();
      e.stopImmediatePropagation();

      var href = link.href;
      drawer.close();

      // 'dialog:close' fires on cart-drawer-component once the close animation
      // finishes. Navigate only then so the drawer is gone before page swap.
      drawer.addEventListener('dialog:close', function () {
        window.location.href = href;
      }, { once: true });
    }, true); // true = capture phase
  }

  // ---------------------------------------------------------------------------
  // bfcache clean-up
  // ---------------------------------------------------------------------------
  // When the browser navigates away it may freeze the page in the back/forward
  // cache with the dialog still open. Two guards:
  //   pagehide  — force-close the dialog BEFORE the snapshot is taken, so
  //               pressing Back restores the page with the drawer already closed.
  //   pageshow  — safety net in case the pagehide close was not enough
  //               (e.g. the page was already frozen before pagehide fired).

  function setupBfcacheHandlers() {
    window.addEventListener('pagehide', function () {
      var dialog = /** @type {HTMLDialogElement | null} */ (document.querySelector('cart-drawer-component dialog[open]'));
      if (!dialog) return;
      // Native close: no animation, instant. Prevents bfcache from capturing
      // the page with an open drawer.
      dialog.close();
      document.body.classList.remove('modal-open');
      document.body.style.top = '';
    });

    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      // Page was restored from bfcache. If the dialog is somehow still open,
      // close it properly (with animation + state reset).
      var drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
      if (drawer && typeof drawer.close === 'function') {
        var dialog = drawer.refs && drawer.refs.dialog;
        if (dialog && dialog.open) {
          drawer.close();
        }
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Variant-strip observer per Nosto container
  // ---------------------------------------------------------------------------

  function observeNostoContainer(id) {
    var el = document.getElementById(id);
    if (!el) return;

    stripVariantFromLinks(el);

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m && m.type === 'childList' && m.addedNodes.length > 0) {
          stripVariantFromLinks(el);
        }
      }
    });

    observer.observe(el, { childList: true, subtree: true });
  }

  // ---------------------------------------------------------------------------
  // Bootstrap
  // ---------------------------------------------------------------------------

  function init() {
    for (var i = 0; i < NOSTO_IDS.length; i++) {
      observeNostoContainer(NOSTO_IDS[i]);
    }

    // Drawer-close and bfcache handlers are document/window-level; they don't
    // need a specific element to exist first.
    setupDrawerCloseOnNavigation();
    setupBfcacheHandlers();

    // Watch for Nosto containers injected after page load.
    var bodyObserver = new MutationObserver(function () {
      for (var i = 0; i < NOSTO_IDS.length; i++) {
        var el = document.getElementById(NOSTO_IDS[i]);
        if (el && !el.dataset.nostoLinkFixObserved) {
          el.dataset.nostoLinkFixObserved = 'true';
          observeNostoContainer(NOSTO_IDS[i]);
        }
      }
    });

    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
